/**
 * agents/consentLogic.ts — the words the set-up screen says about whether an
 * agent may run here, and the one question it is allowed to answer itself.
 *
 * ===========================================================================
 * THE CONJUNCTION IS THE SERVER'S. THIS FILE RENDERS IT.
 * ===========================================================================
 * Founder ruling, 2026-09-08: "Agent capability becomes usable only when all
 * are true: platform tenant enablement · active tenant consent · specific user
 * permission · applicable Agent configuration/deployment authority. Missing any
 * one means unavailable/denied, NEVER IMPLIED CONSENT."
 *
 * Three of those four arrive as SERVER ANSWERS —
 * `GET /api/agents/catalog` reports the enablement standing and the consent
 * standing, and `/auth/me` reports the reader's own grants — and the fourth is
 * the `guard(...)` on each writing route. Nothing here recomputes the verdict:
 * {@link deploymentBlockers} returns the LIST OF REPORTED REFUSALS, each with
 * the sentence and the remedy that belong to it, and a caller that finds the
 * list empty has learned that nothing it was told about is in the way — not
 * that it may deploy. The door decides that, and it decides it again at fire
 * time.
 *
 * That distinction is why this returns a list and not a boolean. A boolean is
 * an answer; a list is a report. "You may deploy" is a sentence this client is
 * not entitled to say.
 *
 * ===========================================================================
 * FOUR STATES, THROUGHOUT, AND THEY ARE FOUR DIFFERENT SENTENCES
 * ===========================================================================
 * "No agents may run here" has four unrelated causes and they send a person to
 * four different places:
 *
 *   Phibian has not offered you the product          → talk to Phibian
 *   your organization has not been asked             → someone here must decide
 *   your organization decided no                     → that decision is yours to change
 *   you may not see / may not decide                 → ask for a grant
 *
 * The last one is the one that gets collapsed. A reader who may not read the
 * consent ledger must be told it is WITHHELD, never that the organization has
 * never decided — those are the `refused-for-scope` and `absent` states this
 * codebase keeps finding collapsed, and on this surface the collapse would tell
 * an organization it never agreed to software that is running on its records.
 */
import type { PillProps } from '@cwos/ui-admin';
import type { agents } from '@cwos/api-client';
import { localDayString } from '../../lib/timeFormat.js';
import type { OrgWideReader, PermissionsState } from '../permissions.js';

/** `agents:configure` — the canonical Agent-governance authority (founder, 2026-09-08). */
export const AGENTS_CONFIGURE_KEY = 'agents:configure';
/** `agents:deploy` — "put an operational agent into this organization AND TURN IT ON". */
export const AGENTS_DEPLOY_KEY = 'agents:deploy';

/**
 * ===========================================================================
 * WHY THIS IS `can()`-SHAPED AND HAS A FIFTH STATE
 * ===========================================================================
 * `authorityGate` answers from `has()`, which is right for a control whose
 * route NARROWS an out-of-scope grant: refusing the reader before the server
 * has been asked would hide a screen the server would have served, narrowed.
 *
 * THE AGENT DOOR DOES NOT NARROW. `agent-runtime/routes.ts:guard` is
 * `can(outcome.principal.authz, permission)` with no scope argument, and
 * `can`'s default is `{ scopeType: 'org' }` — the guard says so in its own
 * words: "An agent is an organization-level actor… A program-scoped
 * `agents:read` would therefore be a grant whose meaning this door cannot
 * honour". A program-scoped `agents:configure` is a 403 on every one of these
 * routes, so the org-wide question is the question the server is about to ask,
 * and `permissions.ts`'s carve-out applies exactly: a predicate that can cite a
 * refusing guard reads `can`.
 *
 * `narrowed` EXISTS BECAUSE A FLATTENED MIRROR CANNOT PRODUCE IT. It is the
 * one state where `has()` and `can()` disagree — the reader holds the key over
 * one program and not over the organization — and it is precisely the state a
 * `has()`-shaped gate would render as `allowed`, offering a control that can
 * only 403. Keeping it as its own member means a fixture can tell the two
 * frames apart: swap `can` for `has` here and the narrowed case stops being
 * reachable, which is a test failure rather than a review finding.
 */
export type AgentAuthority =
  /** The permission set has not arrived. Not a refusal. */
  | { readonly kind: 'unresolved' }
  /** The permission set could not be read. Not a refusal either. */
  | { readonly kind: 'unknown' }
  /** A resolved set that does not contain the key at any scope. */
  | { readonly kind: 'refused'; readonly permission: string }
  /** Held — but only inside a program, and this door is organization-wide. */
  | { readonly kind: 'narrowed'; readonly permission: string }
  | { readonly kind: 'allowed' };

/**
 * The five-state answer to "may this reader take this act against the agent
 * door", asked the way the door asks it.
 *
 * The ORDER matters and mirrors `authorityGate`: an affirmative answer wins
 * over a failed refresh, because a cached set that says yes is still a real
 * answer and a reader who holds the key should keep their controls.
 */
export function agentAuthority(
  state: Pick<PermissionsState, 'has' | 'isLoading' | 'isError'> & OrgWideReader,
  permission: string,
): AgentAuthority {
  if (state.can(permission)) return { kind: 'allowed' };
  if (state.isLoading) return { kind: 'unresolved' };
  if (state.isError) return { kind: 'unknown' };
  // RESOLVED, and `can` said no. `has` now separates the two ways of saying no,
  // and they are not the same sentence: one reader needs a grant, the other
  // needs the grant they already have widened.
  if (state.has(permission)) return { kind: 'narrowed', permission };
  return { kind: 'refused', permission };
}

/** True only for `allowed`. `!open` is the collapse this module exists to prevent. */
export function authorityOpen(authority: AgentAuthority): boolean {
  return authority.kind === 'allowed';
}

/** The sentence for an authority that is not open, or `null` when it is. */
export function agentAuthoritySentence(authority: AgentAuthority, what: string): string | null {
  switch (authority.kind) {
    case 'allowed':
      return null;
    case 'unresolved':
      return `Checking whether you may ${what}…`;
    case 'unknown':
      return `We couldn’t check whether you may ${what}. This is a connection problem, not a decision about your account — nothing here has been withheld from you.`;
    case 'narrowed':
      return `You hold ${authority.permission}, but only within a program. An agent is an organization-level actor, so this door asks for it across the whole organization and will refuse a narrower grant. Ask for it to be widened; asking again as you are will repeat this answer.`;
    case 'refused':
      return `You may not ${what}. This needs the ${authority.permission} permission, which you do not currently hold. It comes with the org_admin role; an administrator who can assign roles can grant it to you.`;
  }
}

// ---------------------------------------------------------------------------
// The two standings, each in its own words
// ---------------------------------------------------------------------------

export interface StandingCopy {
  /** Short label for a pill. */
  readonly label: string;
  /** The fact, in one sentence. */
  readonly headline: string;
  /** What a person does about it, or `null` when there is nothing to do. */
  readonly remedy: string | null;
  /** Whether this standing is one of the reported reasons an agent cannot run. */
  readonly blocking: boolean;
}

// The consent instant's date in the VIEWER's calendar — the UTC date here
// showed consent signed "on" the following day for anyone west of UTC.
const day = (at: number | null): string =>
  at === null ? '' : ` on ${localDayString(new Date(at))}`;

/**
 * PHIBIAN'S HALF. Three states, and `never_enabled` is a measured absence
 * rather than a refusal — nobody has offered and nobody has taken anything
 * away.
 */
export function enablementCopy(standing: agents.AgentEnablementStanding): StandingCopy {
  switch (standing.state) {
    case 'enabled':
      return {
        label: 'Enabled by Phibian',
        headline: `Phibian enabled operational agents for this organization${day(standing.enabledAt)}.`,
        remedy: null,
        blocking: false,
      };
    case 'withdrawn':
      return {
        label: 'Withdrawn by Phibian',
        headline: `Phibian withdrew this organization’s operational-agent entitlement${day(standing.withdrawnAt)}.${
          standing.withdrawalReason ? ` The reason given was: ${standing.withdrawalReason}` : ''
        }`,
        // Not something the organization can fix from this screen, and saying
        // so is the point: a remedy that names the wrong party is worse than
        // none.
        remedy: 'This is Phibian’s decision, not yours. Contact Phibian to discuss it.',
        blocking: true,
      };
    case 'never_enabled':
      return {
        label: 'Not offered',
        headline:
          'Phibian has not enabled operational agents for this organization. Nothing has been taken away — it has not been offered yet.',
        remedy: 'Contact Phibian if you would like operational agents enabled here.',
        blocking: true,
      };
  }
}

/**
 * THE ORGANIZATION'S HALF. Four states.
 *
 * `revoked` is deliberately not worded as "you have not consented". Consent
 * DID exist, for a window the ledger records, and telling an organization that
 * consented for eight months that it never did is a false statement about its
 * own history — `consent.ts` says the same thing on the server side and this
 * is the client half of it.
 */
export function consentCopy(standing: agents.AgentConsentStanding): StandingCopy {
  switch (standing.state) {
    case 'granted':
      return {
        label: 'Consented',
        headline: `This organization consented to operational agents${day(standing.decidedAt)}, acknowledging terms edition ${standing.termsVersion ?? 'unknown'}.`,
        remedy: null,
        blocking: false,
      };
    case 'declined':
      return {
        label: 'Declined',
        headline: `This organization declined operational agents${day(standing.decidedAt)}.`,
        remedy:
          'That decision is this organization’s own to change. Somebody holding agents:configure can record a new one below.',
        blocking: true,
      };
    case 'revoked':
      return {
        label: 'Withdrawn',
        headline: `This organization withdrew its consent to operational agents${day(standing.decidedAt)}. Consent existed before that and the ledger below records the window.`,
        remedy:
          'Agents already deployed can still be viewed, paused and stopped. Nothing new can be configured or deployed, and nothing will fire, until consent is granted again.',
        blocking: true,
      };
    case 'never_asked':
      return {
        label: 'Not decided',
        headline:
          'This organization has not decided whether operational agents may run on its data. Nobody has been asked and nobody has answered.',
        remedy:
          'Somebody holding agents:configure must record that decision below before an agent can be configured or deployed here.',
        blocking: true,
      };
  }
}

/**
 * THE PILLS, and `plain` is doing real work in all three.
 *
 * A measured absence is not an alarm and not a success. `never_enabled` and
 * `never_asked` are the two states where nothing has happened yet, and colouring
 * either of them the way a withdrawal is coloured would tell an administrator
 * that something went wrong when nothing has. `look` is reserved for a decision
 * that was taken and ended something.
 */
export function enablementPill(state: agents.AgentEnablementState): PillProps['variant'] {
  switch (state) {
    case 'enabled':
      return 'good';
    case 'withdrawn':
      return 'look';
    case 'never_enabled':
      return 'plain';
  }
}

export function consentPill(state: agents.AgentConsentState): PillProps['variant'] {
  switch (state) {
    case 'granted':
      return 'good';
    case 'declined':
    case 'revoked':
      return 'look';
    case 'never_asked':
      return 'plain';
  }
}

export function ledgerPill(decision: agents.AgentConsentDecision): PillProps['variant'] {
  switch (decision) {
    case 'granted':
      return 'good';
    case 'revoked':
      return 'look';
    case 'declined':
      return 'plain';
  }
}

/**
 * Whether the acknowledged edition is still the published one.
 *
 * REPORTED AND NOT ACTED ON, which is `terms.ts`'s position and an open
 * founder question rather than an oversight: whether a new edition supersedes
 * an earlier acceptance decides between silently stopping every customer's
 * agents on a terms bump and letting an organization run under terms it has
 * not read. This screen shows the difference and changes nothing because of it.
 *
 * `null` — not `false` — when nothing has been acknowledged. An organization
 * that never decided has not agreed to something out of date.
 */
export function termsCurrencyNote(standing: agents.AgentConsentStanding): string | null {
  if (standing.termsCurrent === null) return null;
  if (standing.termsCurrent) return null;
  return `The edition acknowledged here (${standing.termsVersion ?? 'unknown'}) is not the one currently published. This is shown, not acted on: a later edition does not by itself end a consent already given.`;
}

// ---------------------------------------------------------------------------
// Which decisions the ledger will accept
// ---------------------------------------------------------------------------

/**
 * THE STATE MACHINE, DERIVED FROM THE DATABASE'S THREE RULES rather than
 * invented here.
 *
 * `org_agent_consent_append_only()` (migration 1970000000000) refuses three
 * shapes and each refusal exists because the row would be a FALSE record:
 *
 *   R1 a repeat of the standing decision   → unique_violation (23505)
 *      "recording it again would put a decision in the ledger on a day nobody
 *      decided anything"
 *   R2 `revoked` when the previous decision is not `granted` → 23001
 *      "you cannot withdraw what was never given"
 *   R3 `declined` when the previous decision is `granted` → 23001
 *      "declining after consenting is a REVOCATION and must be recorded as one"
 *
 * Applying those three to the four standings gives exactly this table. It is
 * offered as a CONVENIENCE, not as a second gate: the trigger refuses the
 * others whatever this returns, and `agent-tenant-consent-door.test.ts` is
 * where the two are held to each other.
 */
export function offeredDecisions(
  state: agents.AgentConsentState,
): readonly agents.AgentConsentDecision[] {
  switch (state) {
    // R2 removes `revoked`: there is nothing to withdraw.
    case 'never_asked':
      return ['granted', 'declined'];
    // R1 removes `granted`, R3 removes `declined`. Withdrawing is the only
    // move, and it must be spelled `revoked` so the ledger keeps the fact that
    // consent once existed.
    case 'granted':
      return ['revoked'];
    // R1 removes `declined`, R2 removes `revoked`.
    case 'declined':
      return ['granted'];
    // R1 removes `revoked`. Both of the others are open again.
    case 'revoked':
      return ['granted', 'declined'];
  }
}

/** Why an act is not on offer, in the words of the rule that refuses it. */
export function unofferedReason(
  state: agents.AgentConsentState,
  decision: agents.AgentConsentDecision,
): string | null {
  if (offeredDecisions(state).includes(decision)) return null;
  if (decision === state) {
    return `This organization’s standing is already “${state}”. Recording it again would put a decision in the ledger on a day nobody decided anything.`;
  }
  if (decision === 'revoked') {
    return 'There is no consent to withdraw. An organization that has never consented and one that consented and withdrew are different facts, and a withdrawal row here would make them look the same.';
  }
  return 'This organization has already consented. Withdrawing it is recorded as a revocation, which keeps the fact that consent once existed.';
}

/** What the reader is about to do, in a sentence, before they do it. */
export function decisionLabel(decision: agents.AgentConsentDecision): string {
  switch (decision) {
    case 'granted':
      return 'Consent to operational agents';
    case 'declined':
      return 'Decline operational agents';
    case 'revoked':
      return 'Withdraw this organization’s consent';
  }
}

export function decisionConsequence(decision: agents.AgentConsentDecision): string {
  switch (decision) {
    case 'granted':
      return 'Agents may then be configured and deployed here by people holding the agent permissions. This on its own grants nobody anything: consent is one of four things that must be true.';
    case 'declined':
      return 'This records that the organization was asked and said no. It is not the same as never having been asked, and the ledger keeps both apart.';
    case 'revoked':
      return 'New deployments, new trigger executions and new consequential actions stop immediately, and a run already in flight is refused at its next authority boundary. Existing agents and their history are kept for audit, and can still be paused and stopped.';
  }
}

/** How one recorded decision reads in the history. */
export function ledgerEntryLabel(decision: agents.AgentConsentDecision): string {
  switch (decision) {
    case 'granted':
      return 'Consented';
    case 'declined':
      return 'Declined';
    case 'revoked':
      return 'Withdrew consent';
  }
}

/**
 * What the history IS — and, when it is not all of it, that it is not.
 *
 * "That is the whole history" and "that is the newest hundred" are different
 * claims. The same distinction `AgentAuditPage.hasMore` carries, for the same
 * reason: this is a surface somebody reads to establish what happened.
 */
export function ledgerSentence(ledger: agents.AgentConsentLedger): string {
  const n = ledger.entries.length;
  if (n === 0) {
    return 'No decision has ever been recorded for this organization. This is the whole ledger, and it is empty.';
  }
  const decisions = n === 1 ? '1 decision' : `${n} decisions`;
  return ledger.complete
    ? `${decisions}, oldest first at the bottom. This is the whole ledger.`
    : `The newest ${decisions}. There are older ones this page does not show.`;
}

// ---------------------------------------------------------------------------
// The report — never a verdict
// ---------------------------------------------------------------------------

export interface DeploymentBlocker {
  readonly id: 'enablement' | 'consent';
  readonly headline: string;
  readonly remedy: string | null;
}

/**
 * THE REPORTED REASONS AN AGENT CANNOT RUN HERE, as a list.
 *
 * Two of the conjunction's four terms, because these are the two the catalog
 * response reports. The other two are the reader's own grants, which have their
 * own five-state answer ({@link agentAuthority}) and their own sentences —
 * merging them in here would produce one undifferentiated "you cannot" out of
 * facts about an organization and facts about a person.
 *
 * AN EMPTY LIST IS NOT PERMISSION. It means nothing the server reported is in
 * the way. The door still decides, and it decides again at every fire.
 */
export function deploymentBlockers(catalog: {
  enablement: agents.AgentEnablementStanding;
  consent: agents.AgentConsentStanding;
}): readonly DeploymentBlocker[] {
  const out: DeploymentBlocker[] = [];
  const enablement = enablementCopy(catalog.enablement);
  if (enablement.blocking) {
    out.push({ id: 'enablement', headline: enablement.headline, remedy: enablement.remedy });
  }
  const consent = consentCopy(catalog.consent);
  if (consent.blocking) {
    out.push({ id: 'consent', headline: consent.headline, remedy: consent.remedy });
  }
  return out;
}

/**
 * The line above a disabled form.
 *
 * ONE BLOCKER IS NOT TWO, and neither is worded as the other. "Phibian has not
 * enabled you" and "you have not agreed yet" send an administrator to two
 * different places, and the two are INDEPENDENT PREREQUISITES that may become
 * true in either order — so when both are missing the sentence says both.
 */
export function blockedFormSentence(blockers: readonly DeploymentBlocker[]): string | null {
  if (blockers.length === 0) return null;
  if (blockers.length === 1) {
    return `An agent cannot be configured or deployed here yet, for one reason: ${blockers[0]!.headline}`;
  }
  return `An agent cannot be configured or deployed here yet, and there are ${blockers.length} separate reasons — they are independent of each other and both must be resolved.`;
}

// ---------------------------------------------------------------------------
// Readiness — EVERY prerequisite, each with who can resolve it
// ---------------------------------------------------------------------------

/** `agents:approve` — deciding a step an agent is waiting on. */
export const AGENTS_APPROVE_KEY = 'agents:approve';

export type ReadinessState = 'met' | 'missing' | 'checking' | 'unknown';

export interface ReadinessItem {
  readonly id: 'enablement' | 'consent' | 'agents:configure' | 'agents:deploy' | 'agents:approve';
  readonly label: string;
  readonly state: ReadinessState;
  /** The fact, in one sentence. */
  readonly detail: string;
  /** Who can change it, or `null` when there is nothing to change. */
  readonly resolvedBy: string | null;
}

const KEY_RESOLVER =
  'An administrator who can assign roles. The org_admin role carries this permission.';

function keyItem(
  id: 'agents:configure' | 'agents:deploy' | 'agents:approve',
  label: string,
  authority: AgentAuthority,
  what: string,
): ReadinessItem {
  const state: ReadinessState =
    authority.kind === 'allowed'
      ? 'met'
      : authority.kind === 'unresolved'
        ? 'checking'
        : authority.kind === 'unknown'
          ? 'unknown'
          : 'missing';
  return {
    id,
    label,
    state,
    detail: agentAuthoritySentence(authority, what) ?? `You hold ${id}.`,
    resolvedBy: state === 'missing' ? KEY_RESOLVER : null,
  };
}

/**
 * The readiness panel on /agents: Phibian's offer, the organization's consent
 * and the reader's own three keys, ALL listed whether met or not. The two
 * standings are omitted (not guessed) until they have been read.
 *
 * Like {@link deploymentBlockers} this is a report and not a verdict: the
 * door decides, and decides again at every fire.
 */
export function readinessItems(
  standing: {
    enablement: agents.AgentEnablementStanding;
    consent: agents.AgentConsentStanding;
  } | null,
  authority: {
    configure: AgentAuthority;
    deploy: AgentAuthority;
    approve: AgentAuthority;
  },
): readonly ReadinessItem[] {
  const out: ReadinessItem[] = [];
  if (standing) {
    const enablement = enablementCopy(standing.enablement);
    out.push({
      id: 'enablement',
      label: 'Phibian has enabled agents for your organization',
      state: enablement.blocking ? 'missing' : 'met',
      detail: enablement.headline,
      resolvedBy: enablement.blocking
        ? 'Phibian. A platform operator enables an organization; it cannot be switched on from inside it.'
        : null,
    });
    const consent = consentCopy(standing.consent);
    out.push({
      id: 'consent',
      label: 'Your organization has consented',
      state: consent.blocking ? 'missing' : 'met',
      detail: consent.headline,
      resolvedBy: !consent.blocking
        ? null
        : authorityOpen(authority.configure)
          ? 'You. Record the organization’s decision on Set up an agent.'
          : 'Somebody holding agents:configure (the org_admin role carries it), on Set up an agent.',
    });
  }
  out.push(
    keyItem(
      'agents:configure',
      'You may set up agents and record consent',
      authority.configure,
      'set up agents or record this organization’s consent',
    ),
    keyItem('agents:deploy', 'You may turn agents on', authority.deploy, 'turn an agent on'),
    keyItem(
      'agents:approve',
      'You may approve what an agent is waiting on',
      authority.approve,
      'decide what an agent is waiting on',
    ),
  );
  return out;
}

export function readinessPill(state: ReadinessState): PillProps['variant'] {
  switch (state) {
    case 'met':
      return 'good';
    case 'missing':
      return 'look';
    case 'checking':
    case 'unknown':
      return 'plain';
  }
}

export function readinessLabel(state: ReadinessState): string {
  switch (state) {
    case 'met':
      return 'Ready';
    case 'missing':
      return 'Missing';
    case 'checking':
      return 'Checking';
    case 'unknown':
      return 'Could not check';
  }
}
